import { useEffect } from 'react';
import { usePdfPreview } from '@/features/report-detail';
import { usePdfEngine } from '@/features/pdf-engine';

/**
 * Keeps the report PDF preview warm while the reader is open, so routing to it
 * finds both the document and the renderer already in memory.
 *
 * Holding a live query subscription (rather than a one-shot prefetch) means the
 * save / autosave / finalize mutations, which invalidate the `ReportPdf` tag,
 * refresh the blob automatically.
 *
 * The trade-off is real: each of those invalidations regenerates the PDF
 * server-side, so an autosave-heavy editing session regenerates roughly once
 * per debounce interval. Dropping this hook falls back to fetching on arrival
 * at the preview, at the cost of the wait this exists to remove.
 */
export function useReportPdfPrefetch(studyUuid: string | undefined) {
  usePdfPreview(studyUuid);

  // Compile the 4.3MB pdfium wasm now rather than on the preview's critical
  // path — the engine is shared app-wide and survives navigation.
  const { warmUp } = usePdfEngine();
  useEffect(() => {
    warmUp();
  }, [warmUp]);
}
